import React, { useCallback, useEffect, useState } from "react";
import { Image, Linking } from "react-native";
import Loading from "../Loading";
import Pdf from "../Pdf";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import storage from "../../contexts/FirebaseStorage";
import Text from "../Text";

const fetchDocumentUrl = async (url: string) => {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise<{ base64: string; mimeType: string }>((onSuccess, onError) => {
        try {
            const reader = new FileReader();
            reader.onload = function () {
                onSuccess({
                    base64: (this.result as string).replace(/^data:.+;base64,/, ""),
                    mimeType: response.headers.get("content-type") as string,
                });
            };
            reader.readAsDataURL(blob);
        } catch (e) {
            onError(e);
        }
    });
};

export type DocumentViewerProps = {
    documentUrl: string;
    knownMimeType?: string;
    tintColor?: string;
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    documentUrl,
    knownMimeType,
    tintColor,
}) => {
    const [mimeType, setMimeType] = useState<string | null>();
    const [error, setError] = useState<string | null>();

    const onError = useCallback((error: object) => {
        let errorMessage: string;
        if (error instanceof Error) {
            errorMessage = error.message;
        } else {
            errorMessage = String(error);
        }
        setError(errorMessage);
    }, []);

    useEffect(() => {
        void (async () => {
            if (knownMimeType) {
                setMimeType(knownMimeType);
                return;
            }
            try {
                const ref = documentUrl;
                const fileRef = storage().refFromURL(ref);
                const metadata = await fileRef.getMetadata();
                setMimeType(metadata.contentType);
            } catch (e) {
                // fallback to fetch in worst case
                const data = await fetchDocumentUrl(documentUrl);
                setMimeType(data.mimeType);
            }
        })();
    }, [documentUrl, knownMimeType]);

    return (
        <LoadingBoundary
            error={error}
            clearError={() => {
                setError(null);
            }}
        >
            {mimeType ? (
                mimeType.split("/")[0] === "image" ? (
                    <Image
                        onError={onError}
                        resizeMode="contain"
                        style={{ width: "100%", height: "100%", flex: 1 }}
                        source={{ uri: documentUrl }}
                    />
                ) : mimeType === "application/pdf" ? (
                    <Pdf
                        trustAllCerts={false}
                        source={{
                            uri: documentUrl,
                        }}
                        renderActivityIndicator={(e) => (
                            <Text style={{ color: tintColor }}>{e.toFixed(2)}%</Text>
                        )}
                        onPressLink={(url) => {
                            void Linking.openURL(url);
                        }}
                        onError={onError}
                        style={{
                            flex: 1,
                            backgroundColor: "transparent",
                            width: "100%",
                        }}
                    />
                ) : null
            ) : (
                <Loading />
            )}
        </LoadingBoundary>
    );
};

export default DocumentViewer;
